// SHELL DEL MODO "PROMPT LIBRE" (`/interfaz-v2/modo/prompt-libre`) —
// ADR-2026-09-07-modo-prompt-libre.
//
// El camino directo de imagen de "Crear desde cero", en una pantalla propia: un prompt,
// referencias opcionales (producto / modelo / imagen subida) y el pie de Modelo. Sin
// Ayuda de IA, sin video, sin chat. Es el primero de los tres modos que reemplazan al
// asistente, que sigue existiendo mientras tanto.
//
// Tiene sheet PROPIO —como `AsistenteMarketingSheet`— y no se monta en
// `PresetDetailSheetModelo` ni en `PresetDetailSheet`: los dos son shells de flujos con
// productos y pasos, y este modo no tiene ni pasos ni producto obligatorio. Lo que SÍ
// comparte con Modelo, lo importa: la geometría (`layoutModo.ts`), el pie de desktop
// (`PieConfiguracionModo`, vía `PanelPromptLibre`) y la barra de mobile
// (`BarraInferiorMobile`).
//
// DOS layouts, uno por breakpoint, decididos con `useIsMdUp` (el mismo corte que las
// clases `md:`) para montar UNA sola caja de prompt y no dos con el mismo estado:
//
//   • Desktop: el shell de Modelo — panel de configuración a la izquierda (cabecera +
//     prompt + cards + pie fijo) y, a la derecha, la muestra del modo hasta que haya
//     tandas; después la columna de resultados que arma `InterfazV2`.
//   • Mobile: el cuerpo del asistente de hoy —título, card de referencias, caja de
//     prompt— y el pie de Modelo en el flujo, debajo del scroller.
//
// La generación no vive acá: el sheet arma la PROMESA y se la entrega a `InterfazV2`
// por `onGeneracionDelegada`, el canal de Carrusel/Animar desde el visor. Es la única
// pieza que ya crea la tanda, la cierra con el preload y mapea los errores; duplicarlo
// acá era la alternativa que se descartó. En mobile el sheet suma la pantalla de carga y
// el salto a resultados, como Modelo en mobile.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import LoadingAnimation from '../../../../components/LoadingAnimation';
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
} from '../../../../components/PantallaChrome';
import { DURACION_BARRA_IMAGEN_MS } from '../../../../components/loading/useGenerationProgress';
import type { ResolucionImagen } from '../../../../lib/resolucionImagen';
import {
  signReferenceImage,
  uploadReferenceImage,
  type ChatAttachment,
} from '../../agentAttachments';
import { useAgentEnv } from '../../agentEnvContext';
import { ProductFullMenu } from '../../ChatPanelV2';
import {
  ModelPickerSheet,
  modelSelectionLabel,
  type ModelSelection,
} from '../../ModelPicker';
import { useUserModels } from '../../modelsContext';
import { fotoDeModeloParaReferencia } from '../../modelsApi';
import { MENSAJE_MODELO_SIN_FOTO, ReferenciaIrrecuperable } from '../../referenciasDeGeneracion';
import { conReferenciasFrescas } from '../../../../utils/firmasFrescas';
import { useUserProducts } from '../../productsContext';
import { getProductFullImageUrl } from '../../productsApi';
import { DEFAULT_FORMAT_ID, getFormatLabel } from '../../mockData';
import { caratulaModo, nombreCortoModo } from '../../modesCatalog';
import {
  ANCHO_PANEL_CONFIG,
  COLUMNA_RESULTADOS_AIRE_INTERNO,
  COLUMNA_RESULTADOS_AL_BORDE,
  FILA_MODO_REDISENO,
} from '../../components/layoutModo';
import type { GeneracionDelegada } from '../../components/generacionDelegada';
import { PromptRecientesButton } from '../../components/PromptRecientesButton';
import { recordPromptHistory } from '../../promptHistory';
import { BarraInferiorMobile } from '../../modelo/BarraInferiorMobile';
import { AccionCardsRow } from '../asistente/AccionCardsRow';
import { ReferenciasSheet } from '../asistente/ReferenciasSheet';
import { useIsMdUp } from '../asistente/useIsMdUp';
import { usePegarYSoltarImagenes, VeloSoltarImagen } from '../shared/usePegarYSoltarImagenes';
import { PanelPromptLibre } from './PanelPromptLibre';
import { PromptBoxLibre } from './PromptBoxLibre';
import { miniaturaDeAdjunto } from '../../etiquetaDeAdjunto';
import {
  MAX_CANTIDAD,
  creditosPromptLibre,
  faltaPromptLibre,
  ratioDelFormatoElegido,
  resolverReferencias,
} from './promptLibre';
// La vuelta atrás del modo viaja en la entrada de historial
// (ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste).
import {
  configDelModoAlMontar,
  recordarConfigDelModo,
} from '../shared/volverAlModo';

/** La pantalla de resultados de mobile — la misma a la que salta Modelo en mobile. */
const RUTA_RESULTADOS = '/redes-sociales/imagenes-generadas';

/** El `path` del modo en el catálogo: de acá salen el nombre y la carátula. */
const PATH_MODO = 'prompt-libre';

/** La ruta del modo — la entrada de historial que se sella al irse a los resultados. */
const RUTA_MODO = '/interfaz-v2/modo/prompt-libre';

/** Lo que hace falta para reabrir el modo tal como se generó —
 *  ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
 *  Los ADJUNTOS no van: son archivos con su preview local, no un dato serializable. */
interface ConfigDePromptLibre {
  prompt: string;
  cantidad: number;
}

export interface PromptLibreSheetProps {
  animated: boolean;
  sidebarWidth: number;
  onClose: () => void;
  /** Formato elegido (slug) y el picker compartido de `InterfazV2`. `null` = default. */
  formatoVal: string | null;
  onOpenFormato: () => void;
  /** Calidad (1K/2K/4K), el mismo estado que usan los otros modos. */
  calidad: ResolucionImagen;
  onCalidadChange: (v: ResolucionImagen) => void;
  /**
   * La columna de resultados que arma `InterfazV2` (`PanelTandaCreaciones`), o `null`
   * sin tandas que dibujar. El mismo nodo que los otros shells reciben por
   * `columnaConfiguracion`.
   */
  columnaResultados: React.ReactNode | null;
  /** El canal por el que la tanda entra a la columna — ver el encabezado. */
  onGeneracionDelegada: (g: GeneracionDelegada) => void;
  /** `checkGenerationGuards(undefined, costo)` del padre: plan + saldo + comprometidos. */
  verificarGuards: (costo: number) => Promise<boolean>;
  /**
   * El visor de una creación de la columna está abierto: el panel baja su z-index para
   * no taparlo — el mismo prop que reciben los otros shells (`InterfazV2` lo alimenta
   * con `onVisorChange` de `PanelTandaCreaciones`).
   */
  cederZIndex?: boolean;
}

export const PromptLibreSheet: React.FC<PromptLibreSheetProps> = ({
  animated,
  sidebarWidth,
  onClose,
  formatoVal,
  onOpenFormato,
  calidad,
  onCalidadChange,
  columnaResultados,
  onGeneracionDelegada,
  verificarGuards,
  cederZIndex = false,
}) => {
  const navigate = useNavigate();
  const isMdUp = useIsMdUp();
  const { userId, companyId, refreshModels } = useAgentEnv();
  const userProducts = useUserProducts();
  const userModels = useUserModels();

  // ── Estado del modo ──────────────────────────────────────────────────────
  /**
   * Lo que se estaba generando cuando se salió, si el "Atrás" lo trajo de vuelta —
   * ADR-2026-09-10-los-modos-con-ruta-propia-tambien-vuelven-a-lo-que-generaste.
   */
  const configAlVolver = useRef(configDelModoAlMontar<ConfigDePromptLibre>()).current;
  const [prompt, setPrompt] = useState(configAlVolver?.prompt ?? '');
  // Mismo tipo que el asistente, para reusar sus pickers y su sheet de referencias tal
  // cual. No hay chat detrás.
  const [adjuntos, setAdjuntos] = useState<ChatAttachment[]>([]);
  const [cantidad, setCantidad] = useState(configAlVolver?.cantidad ?? 1);
  const [picker, setPicker] = useState<'none' | 'product' | 'model'>('none');
  const [referenciasOpen, setReferenciasOpen] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  // Mobile: con el teclado abierto el título se desvanece (el mismo gesto del asistente).
  const [inputFocused, setInputFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ── Mobile: pantalla de carga y salto a resultados ───────────────────────
  const [cargando, setCargando] = useState(false);
  const [completar, setCompletar] = useState(false);
  const pendingNavRef = useRef<{ imageIds: string[] } | null>(null);
  // La promesa de generación sobrevive al sheet (vive en el registro del padre); lo
  // que no puede hacer es escribir estado de un componente que ya no está.
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
    };
  }, []);

  // `formatoVal` arranca en `null` hasta que algún modo lo llene; acá se resuelve al
  // default del catálogo para que la etiqueta del pie y el aspecto que viaja digan lo
  // mismo.
  const formatSlug = formatoVal ?? DEFAULT_FORMAT_ID;
  const creditos = creditosPromptLibre(cantidad, calidad);
  const listo = !!userId && !!companyId;
  const falta = faltaPromptLibre({ prompt, listo });

  // ── Mobile: el sheet se fija al visual viewport con el teclado abierto ───
  // El mismo mecanismo del asistente: con el teclado, iOS ENCOGE y SCROLLEA el visual
  // viewport; el sheet es `fixed inset-0` (anclado al layout viewport) y sin compensar
  // el input queda debajo del teclado. La base para detectar el teclado es el MAYOR
  // `vv.height` observado, no `innerHeight` (en iOS es mayor aun sin teclado).
  const [vvStyle, setVvStyle] = useState<React.CSSProperties>({});
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let lastWidth = 0;
    const apply = () => {
      if (window.innerWidth >= 768) {
        setVvStyle({});
        return;
      }
      if (lastWidth !== window.innerWidth) {
        lastWidth = window.innerWidth;
        baseline = vv.height;
      }
      if (vv.height > baseline) baseline = vv.height;
      const kb = Math.max(0, baseline - vv.height);
      setVvStyle(
        kb > 1
          ? {
              top: `${Math.max(0, vv.offsetTop)}px`,
              height: `${vv.height}px`,
              bottom: 'auto',
            }
          : {},
      );
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  // ── Referencias ──────────────────────────────────────────────────────────
  const quitarAdjunto = (id: string) =>
    setAdjuntos((prev) => prev.filter((a) => a.id !== id));

  // Productos del `ProductFullMenu` → chips. Los que ya estaban no se duplican.
  const agregarProductos = (ids: string[]) => {
    setAdjuntos((prev) => {
      const ya = new Set(
        prev
          .filter((a): a is Extract<ChatAttachment, { kind: 'product' }> => a.kind === 'product')
          .map((a) => a.productId),
      );
      const nuevos: ChatAttachment[] = [];
      for (const id of ids) {
        if (ya.has(id)) continue;
        const p = userProducts.find((x) => x.id === id);
        if (!p) continue;
        nuevos.push({
          kind: 'product',
          id: `prod-${id}`,
          productId: id,
          name: p.name,
          imageUrl: p.imageUrl,
        });
      }
      return [...prev, ...nuevos];
    });
    setPicker('none');
  };

  // Modelo del `ModelPickerSheet` → chip ÚNICO (elegir otro reemplaza al anterior),
  // igual que en el asistente.
  const elegirModelo = (sel: ModelSelection) => {
    setAdjuntos((prev) => [
      ...prev.filter((a) => a.kind !== 'model'),
      {
        kind: 'model',
        id: 'model-attachment',
        selection: sel,
        name: modelSelectionLabel(sel),
        imageUrl:
          sel.mode === 'preset'
            ? sel.imageUrl
            : sel.mode === 'upload'
              ? sel.previewUrl
              : null,
      },
    ]);
    setPicker('none');
  };

  // Imagen subida: al storage (ya normaliza HEIC → JPEG) y chip con su preview.
  const subirImagen = async (file: File | null) => {
    if (!file || !companyId) return;
    setSubiendo(true);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(file, companyId);
      if (!vivoRef.current) return;
      setAdjuntos((prev) => [
        ...prev,
        { kind: 'image', id: `img-${Date.now()}`, storagePath, previewUrl },
      ]);
    } catch (e) {
      console.error('[PromptLibre] subir imagen falló', e);
    } finally {
      if (vivoRef.current) setSubiendo(false);
    }
  };

  // Pegar (Ctrl/Cmd+V) y soltar (drag and drop) imágenes en cualquier punto de la
  // pantalla, además del "+" — ADR-2026-09-19-pegar-y-soltar-imagenes-en-la-caja-de-prompt.
  // De a una y en orden, así el "subiendo" de la caja refleja la que está en vuelo.
  const { arrastrando } = usePegarYSoltarImagenes({
    /* Sólo con los pickers CERRADOS —
       ADR-2026-09-21-pegar-y-soltar-no-escucha-con-un-picker-abierto.

       Los listeners viven en `document`, así que con el picker de modelos abierto
       —que tiene su propio "Subir imagen del modelo"— pegar o soltar una foto la
       subía como referencia del modo de ATRÁS, callado y detrás del panel abierto.
       El picker no tiene handler propio de paste/drop, así que no se le robaba nada;
       lo que estaba mal era el destino. */
    activo: picker === 'none',
    onImagenes: (archivos) => {
      void (async () => {
        for (const f of archivos) await subirImagen(f);
      })();
    },
  });

  // ── Generar ──────────────────────────────────────────────────────────────
  const handleGenerar = async () => {
    if (falta || cargando || !userId || !companyId) return;
    // Guards (plan + créditos, con lo comprometido por las tandas en curso) ANTES de
    // pedir nada: si no alcanza, el padre ya mostró su modal.
    const ok = await verificarGuards(creditos);
    if (!ok || !vivoRef.current) return;

    // Lo que viaja se captura AHORA: el prompt y las referencias se quedan en pantalla
    // para iterar, y lo que el usuario cambie después no tiene que colarse en esta
    // tanda.
    const executionId = crypto.randomUUID();
    const promptFinal = prompt.trim();
    const adjuntosDeLaTanda = adjuntos;
    const cantidadDeLaTanda = cantidad;
    const userIdFinal = userId;
    const companyIdFinal = companyId;

    // El prompt se guarda para el menú "Recientes"
    // (ADR-2026-09-11-prompt-libre-recuerda-sus-prompts). Va ACÁ y no adentro de la
    // promesa: lo que interesa es qué escribió el usuario, y una tanda que falla del
    // lado del backend no vuelve el prompt menos reusable —es justo el que va a querer
    // volver a tirar—. Fire-and-forget: nunca demora ni rompe una generación.
    recordPromptHistory(companyIdFinal, 'prompt_libre', promptFinal);

    const resultado = (async () => {
      // El modelo re-firmado y la lista entera con las firmas por vencer renovadas: los
      // chips se arman con fotos firmadas al cargar (una hora), y la pantalla puede
      // llevar más abierta (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat).
      const referenceImages = await conReferenciasFrescas(
        await resolverReferencias(adjuntosDeLaTanda, {
          productoUrl: (id) => getProductFullImageUrl(companyIdFinal, id),
          modeloUrl: async (id) => {
            const m = userModels.find((mm) => String(mm.id) === id);
            if (!m) return null;
            const u = await fotoDeModeloParaReferencia(m);
            // Sin la foto del modelo elegido no se genera: saldría otra persona y se
            // cobraría igual (ADR-2026-10-06-sin-la-foto-del-modelo-no-se-genera).
            if (!u) throw new ReferenciaIrrecuperable(MENSAJE_MODELO_SIN_FOTO);
            return u;
          },
          adjuntoUrl: (path, bucket) => signReferenceImage(path, bucket),
        }),
      );
      // `import()` dinámico, como la card del chat: `agentGenerate` arrastra todo el
      // motor y no tiene por qué entrar al bundle principal por esta pantalla.
      const { generateFreeformImage } = await import('../../agentGenerate');
      const { imageIds } = await generateFreeformImage({
        userId: userIdFinal,
        prompt: promptFinal,
        formatSlug,
        cantidad: cantidadDeLaTanda,
        companyId: companyIdFinal,
        executionId,
        imageSize: calidad,
        modoUtilizado: 'prompt_libre',
        ...(referenceImages.length > 0 ? { referenceImages } : {}),
      });
      // El backend ya descontó los créditos: el pill del shell escucha este evento
      // (lo mismo que dispara la card del chat al terminar).
      try {
        window.dispatchEvent(new Event('atlas:credits-updated'));
      } catch {
        /* noop */
      }
      return imageIds.map(String);
    })();

    onGeneracionDelegada({
      tipo: 'prompt-libre',
      total: cantidadDeLaTanda,
      ratio: ratioDelFormatoElegido(formatSlug),
      producto: '',
      esVideo: false,
      creditos,
      executionIds: [executionId],
      resultado,
    });

    // Mobile: no hay columna donde ver la tanda, así que pantalla de carga y, al
    // terminar, la pantalla de resultados — como Modelo en mobile. El error ya lo mostró
    // el padre (toast o modal); acá sólo se baja la carga.
    if (!isMdUp) {
      pendingNavRef.current = null;
      setCompletar(false);
      setCargando(true);
      resultado.then(
        (ids) => {
          if (!vivoRef.current) return;
          pendingNavRef.current = { imageIds: ids };
          // La vuelta atrás: el "Atrás" desde los resultados devuelve el prompt escrito,
          // no un modo en blanco.
          recordarConfigDelModo<ConfigDePromptLibre>(navigate, RUTA_MODO, {
            prompt,
            cantidad,
          });
          setCompletar(true);
        },
        () => {
          if (!vivoRef.current) return;
          setCargando(false);
          setCompletar(false);
        },
      );
    }
  };

  // ── Piezas compartidas por los dos layouts ───────────────────────────────
  const titulo = nombreCortoModo(PATH_MODO, 'Prompt libre');
  const caratula = caratulaModo(PATH_MODO);
  const migas = [{ label: 'Inicio', onClick: onClose }, { label: titulo }];

  const referenciasThumbs = adjuntos.map((a) => ({
    id: a.id,
    url: miniaturaDeAdjunto(a),
    label: a.kind === 'image' ? 'Imagen' : a.name,
  }));

  const boxProps = {
    prompt,
    onPromptChange: setPrompt,
    onSubmit: handleGenerar,
    adjuntos,
    onQuitarAdjunto: quitarAdjunto,
    subiendo,
    onFocusChange: setInputFocused,
    // "Recientes": los prompts ya usados en ESTE modo, en la esquina inferior izquierda
    // de la caja (ADR-2026-09-11-prompt-libre-recuerda-sus-prompts). Va en los props
    // COMPARTIDOS, así que está en los dos layouts: popover anclado al botón en desktop
    // y bottom sheet en mobile, los dos del propio componente. Se esconde solo mientras
    // no haya historial, así que en una cuenta nueva la fila queda como estaba.
    // `alinear="izquierda"` porque vive de ese lado: por derecha, la lista de 380px se
    // abriría hacia afuera de la caja.
    izquierda: (
      <PromptRecientesButton
        tipo="prompt_libre"
        variante="pill"
        alinear="izquierda"
        titulo="Prompts que usaste"
        onPick={setPrompt}
      />
    ),
  };

  return ReactDOM.createPortal(
    <>
      {/* Sheet. Mobile: `inset-0`, sube desde abajo, DEBAJO del chrome del shell (z-50)
          para que el back y los créditos sigan a la vista.

          Desktop: el mismo panel que los modos-sección de `PresetDetailSheetModelo` —
          a la derecha de la sidebar, con el marco de las pantallas de modo, SIN velo ni
          borde propio: el fondo es el del lienzo (no el de una tarjeta, así se lee como
          sección — ADR-2026-08-03-regla-del-titulo-no-toca-paneles-fixed) y arranca
          debajo de la barra de créditos del shell
          (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra). `lg:pt-[52px]` es el
          aire que la fila de Modelo deja arriba y `lg:!right-0` lo que deja llegar la
          columna de resultados al borde (ver `layoutModo.ts`). Con el visor de una
          creación abierto el panel cede su z-index, como los otros shells
          (`cederZIndex`), para no taparlo. */}
      <div
        className={`fixed inset-0 bg-surface-mobile flex flex-col transition-[transform,opacity] duration-300 ease-out md:left-[var(--sidebar-w,0px)] md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden md:overscroll-contain lg:pt-[52px] lg:!right-0 ${
          cederZIndex ? 'z-40 md:z-30' : 'z-40 md:z-[100]'
        } ${MARCO_PANTALLA} ${
          animated
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-100 md:translate-y-1 md:opacity-0'
        }`}
        style={
          {
            '--sidebar-w': `${sidebarWidth}px`,
            ...(isMdUp
              ? {
                  top: 'var(--shell-barra-creditos, 0px)',
                  background: 'var(--plantillas-sticky-bg, #FBFBFD)',
                }
              : vvStyle),
          } as React.CSSProperties
        }
      >
        <VeloSoltarImagen visible={arrastrando} />
        {isMdUp ? (
          // ── DESKTOP: el shell de Modelo ──────────────────────────────────
          <div
            className={`flex-1 min-h-0 px-8 pt-6 lg:pt-0 pb-6 flex gap-6 items-stretch ${FILA_MODO_REDISENO}`}
          >
            <aside
              style={{ flexBasis: ANCHO_PANEL_CONFIG, width: ANCHO_PANEL_CONFIG }}
              className="shrink-0 grow-0 min-h-0 flex flex-col bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.04] overflow-hidden"
            >
              {/* La cabecera del panel, FUERA del scroller — la misma de Modelo
                  (`cabeceraEnPanel`): breadcrumb y el nombre del modo. `<h2>` y no
                  `<h1>`: `.shell-seccion h1` fuerza 32px/700 con `!important`. */}
              <div className="px-6 pt-5 pb-1">
                <EncabezadoPantalla
                  migas={migas}
                  className="!px-0 !pt-0 !pb-2"
                  respaldo={
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Volver
                    </button>
                  }
                />
                <h2 className="text-[23px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                  {titulo}
                </h2>
              </div>
              <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 pt-2">
                <PanelPromptLibre
                  // `marco="plano"` SÓLO acá: la caja es el primer hijo del scroller
                  // del panel (`overflow-y-auto` sin padding), que recorta todo lo que
                  // se pinte AFUERA del border box — el anillo y la sombra del marco
                  // destacado se perdían arriba y a los costados y sólo se veían abajo.
                  // En mobile el scroller tiene `px-5` y no recorta nada, así que ahí
                  // sigue el destacado.
                  box={{ ...boxProps, marco: 'plano' }}
                  onAddProduct={() => setPicker('product')}
                  onAddModel={() => setPicker('model')}
                  onUploadImage={() => fileInputRef.current?.click()}
                  calidad={calidad}
                  onCalidadChange={onCalidadChange}
                  formatoLabel={getFormatLabel(formatSlug)}
                  onOpenFormato={onOpenFormato}
                  cantidad={cantidad}
                  onCantidadChange={(v) =>
                    setCantidad(Math.max(1, Math.min(MAX_CANTIDAD, v)))
                  }
                  creditos={creditos}
                  onGenerate={handleGenerar}
                  falta={falta}
                />
              </div>
            </aside>

            {/* La columna de la derecha: la muestra del modo hasta que haya algo
                generado; las tandas la reemplazan en cuanto existen
                (ADR-2026-08-21-modelo-v2-la-muestra-se-queda). Con tandas se sale de los
                paddings y llega al marco, como en Modelo
                (ADR-2026-09-06-la-columna-de-resultados-llega-al-marco). */}
            <div
              className={`flex-1 min-w-0 min-h-0 flex flex-col ${
                columnaResultados ? COLUMNA_RESULTADOS_AL_BORDE : ''
              }`}
            >
              {columnaResultados ? (
                <div
                  className={`flex-1 min-h-0 overflow-y-auto ${COLUMNA_RESULTADOS_AIRE_INTERNO}`}
                >
                  {columnaResultados}
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 px-6">
                  <div className="shrink-0 text-center max-w-[440px] mb-6">
                    <h2 className="text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black/90">
                      Describí la imagen que querés
                    </h2>
                    <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
                      Atlas la genera en segundos
                    </p>
                  </div>
                  {/* La carátula del catálogo: el clip si lo hay, quieto en su póster. */}
                  {caratula.video ? (
                    <video
                      src={caratula.video}
                      poster={caratula.poster}
                      autoPlay
                      muted
                      loop
                      playsInline
                      className="w-full max-w-[560px] max-h-[480px] h-auto object-contain rounded-2xl"
                    />
                  ) : (
                    <img
                      src={caratula.poster}
                      alt={titulo}
                      className="w-full max-w-[560px] max-h-[480px] h-auto object-contain rounded-2xl"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          // ── MOBILE: el cuerpo del asistente + el pie de Modelo ───────────
          // `pt`: safe-area + el alto del chrome del shell (back / créditos / menú), que
          // flota encima del sheet.
          <div className="relative flex-1 min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+3.5rem)]">
            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-5 pb-4">
              {/* El título ocupa el aire de arriba y se centra; con el teclado abierto
                  se desvanece para dejarle lugar al prompt. Dos líneas: la primera en la
                  fuente de la app y la segunda en la display serif itálica, la misma
                  pareja de "Creá / sin límites". */}
              <div
                className={`flex-1 min-h-[120px] flex items-center justify-center transition-opacity duration-300 ${
                  inputFocused ? 'opacity-0' : 'opacity-100'
                }`}
              >
                <h1 className="text-[32px] font-normal text-center leading-tight text-brand-black">
                  <span className="block font-sans font-semibold">Describí</span>
                  <span className="block font-display italic">tu imagen</span>
                </h1>
              </div>
              {/* Card única de referencias + las miniaturas de lo elegido. Padre BLOCK
                  a propósito: el carrusel se sale a los bordes con `-mx-5`. */}
              <div className="w-full">
                <AccionCardsRow
                  onOpenReferencias={() => setReferenciasOpen(true)}
                  referencias={referenciasThumbs}
                  onRemoveReferencia={quitarAdjunto}
                />
              </div>
              {/* La caja, abajo del todo. Sin chips: las referencias ya se ven en la
                  tira de arriba. */}
              <div className="mt-3">
                <PromptBoxLibre {...boxProps} mostrarChips={false} />
              </div>
            </div>
            {/* El pie de Modelo, EN EL FLUJO como hermano del scroller: Calidad y
                Formato a la vista, la cantidad pegada al Generar
                (ADR-2026-09-03-modelo-mobile-toma-el-pie-de-desktop). */}
            <BarraInferiorMobile
              calidad={calidad}
              onCalidadChange={onCalidadChange}
              formatoLabel={getFormatLabel(formatSlug)}
              onOpenFormato={onOpenFormato}
              cantidad={cantidad}
              cantidadMax={MAX_CANTIDAD}
              onCantidadChange={(v) =>
                setCantidad(Math.max(1, Math.min(MAX_CANTIDAD, v)))
              }
              imagenesDeLaTanda={cantidad}
              creditCost={creditos}
              onGenerate={handleGenerar}
              falta={falta}
            />
          </div>
        )}
      </div>

      {/* Input de archivo oculto — "Subir imagen". Se resetea tras cada elección para
          poder volver a elegir el mismo archivo. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          void subirImagen(f);
          e.target.value = '';
        }}
      />

      {/* Los pickers reales, los mismos del asistente y del modo Modelo. */}
      {picker === 'product' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={() => setPicker('none')}
          onAccept={agregarProductos}
        />
      )}
      <ModelPickerSheet
        open={picker === 'model'}
        selection={null}
        onChange={elegirModelo}
        onClose={() => setPicker('none')}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        hideAuto
      />

      {/* Sheet único de referencias — MOBILE (Subidas / Productos / Modelos). "Utilizar"
          trae la selección COMPLETA de productos, así que se sincroniza en los dos
          sentidos: se quitan los chips deseleccionados y se suman los nuevos. `model`
          en `null` = no se tocó. */}
      <ReferenciasSheet
        open={referenciasOpen}
        onClose={() => setReferenciasOpen(false)}
        attachments={adjuntos}
        onRemoveAttachment={quitarAdjunto}
        models={userModels}
        onUploadImage={() => fileInputRef.current?.click()}
        onUtilizar={({ products, model }) => {
          const elegidos = new Set(products.map((p) => p.id));
          setAdjuntos((prev) =>
            prev.filter((a) => a.kind !== 'product' || elegidos.has(a.productId)),
          );
          agregarProductos(products.map((p) => p.id));
          if (model) elegirModelo(model);
        }}
        uploading={subiendo}
      />

      {/* Mobile: la pantalla de carga de siempre (80s de barra) y, al completar, el
          salto a resultados. En desktop nunca se monta: la tanda se ve en la columna. */}
      {cargando && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={DURACION_BARRA_IMAGEN_MS}
          completeNow={completar}
          onCompleted={() => {
            setCargando(false);
            setCompletar(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend && companyId) {
              navigate(RUTA_RESULTADOS, {
                state: { imageIds: pend.imageIds, companyId },
              });
            }
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default PromptLibreSheet;
